// cm-coding: Connector administrators must see exactly the Workday filters they
// are authorized to change, while other workspace users may only inspect that
// scope. Render both modes from the same filter rows so read-only disclosure
// cannot drift from the values the privileged editor saves.

import { Plus, X } from 'lucide-react'

import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui'
import type { WorkdayFilter, WorkdayFilterField } from '../types'
import { workdayFilterLabels } from '../types'

const fields = Object.keys(workdayFilterLabels) as WorkdayFilterField[]

export function WorkdayFilters({
  filters,
  canEdit,
  onChange,
}: {
  filters: WorkdayFilter[]
  canEdit: boolean
  onChange: (filters: WorkdayFilter[]) => void
}) {
  const update = (index: number, value: WorkdayFilter) => {
    onChange(filters.map((item, position) => position === index ? value : item))
  }
  const add = () => {
    const field = fields.find(candidate => !filters.some(item => item.field === candidate))
    if (!field) return
    onChange([...filters, {
      field,
      operator: field === 'attachment_filename' ? 'starts-with' : 'is',
      value: '',
    }])
  }
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-700">Requisition filters</p>
          <p className="mt-0.5 text-xs text-slate-500">All criteria must match.</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          disabled={!canEdit || filters.length >= 7}
          onClick={add}
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add filter
        </Button>
      </div>
      {filters.map((filter, index) => (
        <div
          key={`${index}-${filter.field}`}
          className="grid items-center gap-2 sm:grid-cols-[52px_minmax(0,1fr)_128px_minmax(0,1.3fr)_32px]"
        >
          <span className="text-xs text-slate-400">{index === 0 ? 'Where' : 'and'}</span>
          <Select
            value={filter.field}
            disabled={!canEdit}
            onValueChange={value => {
              const field = value as WorkdayFilterField
              update(index, {
                field,
                operator: field === 'attachment_filename' ? 'starts-with' : 'is',
                value: '',
              })
            }}
          >
            <SelectTrigger aria-label={`Workday filter ${index + 1} field`} className="h-9 bg-white text-xs">
              {workdayFilterLabels[filter.field]}
            </SelectTrigger>
            <SelectContent>
              {fields.map(field => (
                <SelectItem
                  key={field}
                  value={field}
                  disabled={filters.some((item, position) => item.field === field && position !== index)}
                >
                  {workdayFilterLabels[field]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            aria-label={`Workday filter ${index + 1} operator`}
            disabled
            value={filter.operator === 'starts-with' ? 'starts with' : 'is'}
            className="h-9 bg-white text-xs"
          />
          <Input
            aria-label={`Workday filter ${index + 1} value`}
            disabled={!canEdit}
            value={filter.value}
            maxLength={513}
            className="h-9 bg-white text-xs"
            onChange={event => update(index, { ...filter, value: event.target.value })}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove Workday filter ${index + 1}`}
            disabled={!canEdit}
            className="h-9 w-8 text-slate-400 hover:text-destructive"
            onClick={() => onChange(filters.filter((_, position) => position !== index))}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  )
}
